import React from 'react';

import {useSnackbar} from 'notistack';

import {
    Constants,
    HistoryRoutes,
    Messages,
    SnackbarVariants,
    StatusRegisterMessages,
} from 'commons/constants';
import {useAlert, useSpinner} from 'hooks';
import {MainContent} from 'layouts/commons';
import {useAnalytics} from 'hooks/AnalyticsHook';
import {ContractStatus, EfeModalMsg} from 'components';
import {ClientService, DocumentServices} from 'services';
import {getBase64Extension} from 'commons/utils/Functions';
import {useMenuContext} from 'context/onboarding-menu/OnboardingMenu';
import {EfeResponse, ExpedientResponse, UploadDocumentServiceRequest} from 'commons/models';
import DigitalizedSignatureForm from '../digitalized-sign-form/DigitalizedSignatureForm';
import {DigitalizedSignatureValues} from 'commons/models/modules/content/DigitalizedSignatureValues';
import {useUserContext} from 'context/user/UserContext';

function ServiceContract() {
    const {sendPageView} = useAnalytics();
    const {enqueueSnackbar} = useSnackbar();
    const {expedientId, fatherKycId} = useMenuContext();
    const {user} = useUserContext();
    const {showAlert, defaultError, genericError, showAlertError, closeAlert} = useAlert();

    const [isLoading, setIsLoading] = React.useState(false);
    const [userBlocked, setUserBlocked] = React.useState<boolean>(true);
    const [showSignaturePad, setShowSignaturePad] = React.useState(false);
    const [dashboardBlocked, setDashboardBlocked] = React.useState<boolean>(true);
    const [message, setMessage] = React.useState<string>(
        StatusRegisterMessages.CONTRACT_IN_REVISION
    );

    async function getExpedientStatus(): Promise<ExpedientResponse | null> {
        let state = null;
        try {
            const response: EfeResponse<ExpedientResponse> = await ClientService.getExpedientStatus(
                expedientId
            );
            if (response && response.data) {
                const data: ExpedientResponse = response.data;
                state = data;
            } else {
                showAlertError(undefined, Messages.COMMON_ERROR);
            }
        } catch (error) {
            showAlertError(error, Messages.COMMON_ERROR);
        }
        return state;
    }

    async function getAccountData(): Promise<boolean> {
        let status = false;
        try {
            const response = await ClientService.getAccountData(user.fullName.trim());
            if (
                response.data &&
                response.data.message &&
                Constants.ACCOUNT_STATUS_OK === response.data.message
            ) {
                status = true;
            }
        } catch (error) {
            showAlertError(error, Messages.COMMON_ERROR);
        }
        return status;
    }

    React.useEffect(() => {
        if (fatherKycId > 0 && expedientId) {
            (async function () {
                setIsLoading(true);
                try {
                    const status = await getExpedientStatus();
                    if (status && Constants.AML_AUTHORIZED_ID === status?.stateKey) {
                        if (status && status.swContractSignature) {
                            setUserBlocked(false);
                            setDashboardBlocked(false);
                            setMessage(StatusRegisterMessages.PROCESS_OK);
                        } else {
                            setUserBlocked(false);
                        }
                    }
                    if (status && !status.swContractSignature && (await getAccountData())) {
                        setShowSignaturePad(true);
                    }
                } finally {
                    setIsLoading(false);
                }
            })();
        }
    }, []);

    async function sendSignature(value: DigitalizedSignatureValues) {
        setIsLoading(true);
        try {
            const fileConverted = await (await fetch(value.signature)).blob();
            const extension = getBase64Extension(value.signature);
            const request: UploadDocumentServiceRequest = {
                documentKey: Constants.DOC_FIRMA_FINAL,
                expedientId: expedientId,
                file: fileConverted,
                kycId: fatherKycId,
                filename: `${Constants.DOC_FIRMA_FINAL}.${extension}`,
            };
            const response: EfeResponse<any> = await DocumentServices.updateDocument(request);
            if (response && response.data) {
                setIsLoading(false);
                enqueueSnackbar(Messages.FILE_UPLOAD_OK, {
                    variant: SnackbarVariants.SUCCESS,
                });
                setShowSignaturePad(false);
                setDashboardBlocked(false);
                setMessage(StatusRegisterMessages.PROCESS_OK);
                sendPageView(HistoryRoutes.COMPLETED_REGISTER);
            } else {
                setIsLoading(false);
                enqueueSnackbar(Messages.FILE_NOT_UPLOAD, {
                    variant: SnackbarVariants.ERROR,
                });
            }
        } catch (error) {
            setIsLoading(false);
            enqueueSnackbar(Messages.FILE_NOT_UPLOAD, {
                variant: SnackbarVariants.ERROR,
            });
        }
    }

    return (
        <MainContent>
            {showAlert && (
                <EfeModalMsg
                    error={genericError}
                    open={showAlert}
                    defaultError={defaultError}
                    handleAction={closeAlert}
                />
            )}
            {isLoading && useSpinner(isLoading)}
            {showSignaturePad ? (
                <DigitalizedSignatureForm submitHandler={sendSignature} />
            ) : (
                <ContractStatus
                    message={message}
                    kycId={fatherKycId}
                    expedientId={expedientId}
                    userBlocked={userBlocked}
                    dashboardBlocked={dashboardBlocked}
                    documentKey={Constants.DOC_CONTRATO_CL_PDF}
                />
            )}
        </MainContent>
    );
}

export default ServiceContract;
